import {
  CropIcon,
  EraserIcon,
  type IconComponent,
  ImageUpscaleIcon,
  LanguagesIcon,
  RemoveBackgroundIcon,
  RotateCwSquareIcon,
  WandSparklesIcon,
  ZapIcon,
} from '@air/design-icons';

import { type PercentageRect } from '~/components/EditMode/components/BoundingBoxLayerKonva/types';
import { type AssetTransformType } from '~/components/EditMode/types';

/**
 * Transforms the backend runs through a fal model, so they spend workspace credits. The rest are
 * imgix-backed and free — gating those on depleted credits would be a regression.
 *
 * Listed explicitly rather than derived from `method: 'server'`, which `upscale` also uses for free.
 */
export const CREDIT_CONSUMING_ASSET_TRANSFORM_TYPES: ReadonlySet<AssetTransformType> = new Set(['outpaint', 'prompt']);

export const FULL_BOUNDING_BOX: PercentageRect = {
  pctStartX: 0,
  pctStartY: 0,
  pctEndX: 1,
  pctEndY: 1,
};

export type AssetTransformStatus = 'idle' | 'in-progress' | 'completed' | 'saving' | 'failed' | 'idle-retry';
export type AssetTransformMethod = 'client' | 'server' | 'canvas';

/** Not every panel option runs a transform on the asset — `translate` hands the image off to a canvas. */
type EditModeOptionId = AssetTransformType | 'translate';

export type EditModeOption = {
  id: EditModeOptionId;
  label: string;
  shortPhrase: string;
  actionPhrase: string;
  icon: IconComponent;
  method: AssetTransformMethod;
  enabled?: boolean;
  confirmButtonLabel: string;
};

export const EDIT_MODE_OPTION_REMOVE_OBJECT: EditModeOption = {
  id: 'removeObject',
  label: 'Erase elements',
  shortPhrase: 'Erase elements',
  actionPhrase: 'Erasing elements',
  icon: EraserIcon,
  method: 'server',
  confirmButtonLabel: 'Erase selection',
};

export const EDIT_MODE_OPTION_REPLACE_BACKGROUND: EditModeOption = {
  id: 'replaceBackground',
  label: 'Remove background',
  shortPhrase: 'Remove background',
  actionPhrase: 'Removing background',
  icon: RemoveBackgroundIcon,
  method: 'server',
  confirmButtonLabel: 'Remove background',
};

export const EDIT_MODE_OPTION_CROP: EditModeOption = {
  id: 'crop',
  label: 'Crop',
  shortPhrase: 'Crop',
  actionPhrase: 'Cropping',
  icon: CropIcon,
  method: 'client',
  confirmButtonLabel: 'Apply crop',
};

export const EDIT_MODE_OPTION_ROTATE: EditModeOption = {
  id: 'orient',
  label: 'Flip and rotate',
  shortPhrase: 'Flip and rotate',
  actionPhrase: 'Transforming',
  icon: RotateCwSquareIcon,
  method: 'client',
  confirmButtonLabel: 'Apply transform',
};

export const EDIT_MODE_OPTION_EXTEND_BACKGROUND: EditModeOption = {
  id: 'outpaint',
  label: 'Extend background',
  shortPhrase: 'Extend background',
  actionPhrase: 'Extending background',
  icon: ImageUpscaleIcon,
  method: 'server',
  confirmButtonLabel: 'Extend background',
};

export const EDIT_MODE_OPTION_TRANSLATE: EditModeOption = {
  id: 'translate',
  label: 'Translate',
  shortPhrase: 'Translate',
  actionPhrase: 'Translating',
  icon: LanguagesIcon,
  method: 'canvas',
  confirmButtonLabel: 'Continue in Canvas',
};

export const EDIT_MODE_OPTION_UPSCALE: EditModeOption = {
  id: 'upscale',
  label: 'Upscale resolution',
  shortPhrase: 'Upscale resolution',
  actionPhrase: 'Upscaling',
  icon: WandSparklesIcon,
  method: 'server',
  confirmButtonLabel: 'Apply upscale',
};

export const EDIT_MODE_OPTION_CUSTOM_PROMPT: EditModeOption = {
  id: 'prompt',
  label: 'Custom prompt',
  shortPhrase: 'Custom prompt',
  actionPhrase: 'Processing prompt',
  icon: ZapIcon,
  method: 'server',
  confirmButtonLabel: 'Generate',
};

export const AVAILABLE_PRO_EDIT_MODE_OPTIONS = new Set([EDIT_MODE_OPTION_CROP.id, EDIT_MODE_OPTION_ROTATE.id]);
